import { lazy, Suspense, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Outlet, useLocation } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { SiteProvider } from "./context/SiteContext";
import { Header } from "./components/Header";
import { Footer } from "./components/Footer";
import { track } from "./lib/analytics";
import Home from "./pages/Home";
import Explore from "./pages/Explore";
import CreateWizard from "./pages/CreateWizard";
import MemorialPage from "./pages/MemorialPage";
import ManagePage from "./pages/ManagePage";
import ListPage from "./pages/ListPage";
import ContactPage from "./pages/ContactPage";
import { RecoverPage, RecoverConfirm, VerifyEmail, LegalPage, NotFound } from "./pages/MiscPages";

const EnvironmentView = lazy(() => import("./pages/EnvironmentView"));
const AdminApp = lazy(() => import("./admin/AdminApp"));

const Loading = () => (
  <div className="min-h-screen grid place-items-center bg-[hsl(var(--background))]" data-testid="route-loading">
    <div className="boot-glow" aria-hidden="true" />
  </div>
);

const OVERLAY = [/^\/$/, /^\/m\//];

function PageTracker() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
    if (!pathname.startsWith("/admin")) track("pageview");
  }, [pathname]);
  return null;
}

function PublicLayout() {
  const { pathname } = useLocation();
  return (
    <>
      <Header overlay={OVERLAY.some((r) => r.test(pathname))} />
      <Outlet />
      <Footer />
    </>
  );
}

function App() {
  return (
    <div className="App">
      <SiteProvider>
        <BrowserRouter>
          <PageTracker />
          <Suspense fallback={<Loading />}>
            <Routes>
              <Route path="/explore/:env" element={<EnvironmentView />} />
              <Route path="/admin/*" element={<AdminApp />} />
              <Route element={<PublicLayout />}>
                <Route index element={<Home />} />
                <Route path="explore" element={<Explore />} />
                <Route path="create" element={<CreateWizard />} />
                <Route path="m/:slug" element={<MemorialPage />} />
                <Route path="manage/:token" element={<ManagePage />} />
                <Route path="memorials" element={<ListPage />} />
                <Route path="contact" element={<ContactPage />} />
                <Route path="recover" element={<RecoverPage />} />
                <Route path="recover/:token" element={<RecoverConfirm />} />
                <Route path="verify-email/:token" element={<VerifyEmail />} />
                <Route path="privacy" element={<LegalPage kind="privacy" />} />
                <Route path="cookies" element={<LegalPage kind="cookies" />} />
                <Route path="terms" element={<LegalPage kind="terms" />} />
                <Route path="*" element={<NotFound />} />
              </Route>
            </Routes>
          </Suspense>
        </BrowserRouter>
        <Toaster position="bottom-center" richColors />
      </SiteProvider>
    </div>
  );
}

export default App;
